import { Settings, Info, LogIn, LogOut, GraduationCap, Users, Swords, Award } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.ts';
import { useProfile } from '../hooks/useProfile.ts';
import Avatar from './Avatar.tsx';

function SheetItem({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        width: '100%', padding: '12px 14px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: 'var(--ink)', fontFamily: 'var(--font-ui)',
        fontSize: 15, fontWeight: 500,
        borderRadius: 'var(--radius-card)',
        transition: 'background .12s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ color: 'var(--ink-soft)' }}>{icon}</span>
      {label}
    </button>
  );
}

export default function MoreSheet({ onClose, onOpenSettings, onNavigate }: {
  onClose: () => void;
  onOpenSettings: () => void;
  onNavigate: (path: string) => void;
}) {
  const { isAuthenticated, isLoading, user, login, logout } = useAuth();
  const { username: profileUsername, avatarFilename: profileAvatarFilename } = useProfile();

  function action(fn: () => void) {
    fn();
    onClose();
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop"
        onClick={onClose}
        style={{
          zIndex: 60,
          animation: 'fadeIn .15s ease',
        }}
      />
      {/* Sheet */}
      <div
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 'calc(env(safe-area-inset-bottom, 0px))',
          zIndex: 61,
          background: 'var(--surface)',
          borderTop: '1px solid var(--line)',
          borderRadius: 'var(--radius-card) var(--radius-card) 0 0',
          padding: '12px 12px 8px',
          boxShadow: '0 -4px 24px rgba(0,0,0,.1)',
          animation: 'sheet-up .2s ease',
        }}
      >
        {!isLoading && (
          isAuthenticated ? (
            <>
              <SheetItem
                icon={<Avatar username={profileUsername ?? user?.name ?? ''} avatarFilename={profileAvatarFilename} size={16} />}
                label="Profile"
                onClick={() => action(() => onNavigate('/profile'))}
              />
              <SheetItem
                icon={<LogOut size={16} strokeWidth={1.5} />}
                label="Log out"
                onClick={() => action(logout)}
              />
            </>
          ) : (
            <SheetItem
              icon={<LogIn size={16} strokeWidth={1.5} />}
              label="Log in"
              onClick={() => action(login)}
            />
          )
        )}
        <div style={{ height: 1, background: 'var(--line)', margin: '4px 14px' }} />
        <SheetItem icon={<Users size={16} strokeWidth={1.5} />} label="Community"
          onClick={() => action(() => onNavigate('/community'))} />
        <SheetItem icon={<Award size={16} strokeWidth={1.5} />} label="Achievements"
          onClick={() => action(() => onNavigate('/achievements'))} />
        <SheetItem icon={<GraduationCap size={16} strokeWidth={1.5} />} label="Lessons"
          onClick={() => action(() => onNavigate('/lessons'))} />
        <SheetItem icon={<Info size={16} strokeWidth={1.5} />} label="About"
          onClick={() => action(() => onNavigate('/about'))} />
        <SheetItem icon={<Settings size={16} strokeWidth={1.5} />} label="Settings"
          onClick={() => action(onOpenSettings)} />
      </div>
    </>
  );
}
